import type { Role } from '@bia-auction/shared-schema';
import { tokenResponseSchema } from '@bia-auction/shared-schema';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { api } from './api';
import { decodeIdentity } from './identity';
import {
  clearTokens,
  ensureAccess,
  getAccessToken,
  isAuthenticated,
  setTokens,
  subscribe,
} from './token-store';

export interface AuthState {
  userId: string | null;
  roles: Role[];
  authenticated: boolean;
}

interface AuthContextValue {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Re-render on any token change; the snapshot is a stable primitive.
  const marker = useSyncExternalStore(
    subscribe,
    () => `${getAccessToken() ?? ''}|${isAuthenticated()}`,
    () => '|false',
  );

  // A persisted refresh token but no access token yet (fresh load) → hydrate the access token
  // so role-gating has the claims.
  useEffect(() => {
    if (isAuthenticated() && !getAccessToken()) void ensureAccess();
  }, []);

  const state = useMemo<AuthState>(() => {
    const { userId, roles } = decodeIdentity();
    return { userId, roles, authenticated: isAuthenticated() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api(
      '/auth/login',
      { method: 'POST', body: { email, password }, auth: false },
      tokenResponseSchema,
    );
    setTokens(res);
  }, []);

  const logout = useCallback(() => clearTokens(), []);

  const value = useMemo(() => ({ state, login, logout }), [state, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
